import type { FormatMessage } from "@shared/i18n-react-intl";
import type { AppIconName } from "@shared/ui/chakra/data-display/icon/icon";

import { type ComponentProps, useEffect } from "react";

import { Flex, VStack } from "@chakra-ui/react";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";

import { Banner, CellPanel } from "@shared/ui/atoms";
import { Icon } from "@shared/ui/chakra/data-display/icon";
import { Typography } from "@shared/ui/chakra/typography/typography";
import { Cell } from "@shared/ui/organisms/cell";

import {
  kybVerifyCaptionMessages,
  kycVerifyCaptionMessages,
  securityAlertMessages,
  twoFaFeatureMessages,
} from "./messages";

export type SecurityRequirement =
  | "kyb-verify"
  | "kyc-verify"
  | "offer"
  | "phone"
  | "policies"
  | "questionnaire"
  | "two-fa";

export type VerifyStatus =
  | "blocked"
  | "checking"
  | "completed"
  | "pause"
  | "rejected"
  | "requested"
  | "revision";

export type SecuritySnapshot = {
  authenticatorEnabled: boolean;
  offerAccepted: boolean;
  phoneChecked: boolean;
  policiesAccepted: boolean;
  questionnairePassed: boolean;
};

type Props = {
  completedRequirements: SecurityRequirement[];
  description: MessageDescriptor;
  myRefetch?: VoidFunction;
  requirements: SecurityRequirement[];
  security: SecuritySnapshot;
  verificationStatus?: VerifyStatus;
  onClick: (requirement: SecurityRequirement) => void;
  onCompleted: () => void;
};

type SecurityRequirementConfigArgs = SecuritySnapshot & {
  status?: VerifyStatus;
};

type RequirementConfig = {
  canClick: boolean;
  caption?: MessageDescriptor;
  captionColor?: string;
  iconColor?: string;
  iconName: AppIconName;
  name: MessageDescriptor;
};

type SecurityRequirementConfig = (
  args: SecurityRequirementConfigArgs,
) => RequirementConfig;

const blockedConfig = (name: MessageDescriptor): RequirementConfig => ({
  canClick: false,
  caption: twoFaFeatureMessages.blocked,
  captionColor: "text.fg_error",
  iconColor: "fg.subtle",
  iconName: "Lock",
  name,
});

const suspendedConfig = (name: MessageDescriptor): RequirementConfig => ({
  canClick: false,
  caption: twoFaFeatureMessages.pauseTwoFa,
  captionColor: "text.fg_warning",
  iconColor: "fg.subtle",
  iconName: "Pause",
  name,
});

const completedConfig = (name: MessageDescriptor): RequirementConfig => ({
  canClick: false,
  caption: twoFaFeatureMessages.requirementCompleted,
  captionColor: "text.fg_success",
  iconColor: "fg.success",
  iconName: "Check",
  name,
});

const bannerConfigMap: Partial<
  Record<
    VerifyStatus,
    (formatMessage: FormatMessage) => ComponentProps<typeof Banner>
  >
> = {
  blocked: (formatMessage) => ({
    description: formatMessage(securityAlertMessages.blockedDescription),
    iconName: "Alert",
    title: formatMessage(securityAlertMessages.blockedTitle),
    variant: "errorSolid",
  }),
  pause: (formatMessage) => ({
    description: formatMessage(securityAlertMessages.suspendedDescription),
    iconName: "ShieldDone",
    title: formatMessage(securityAlertMessages.suspendedTitle),
    variant: "warning",
  }),
};

const kycVerifyMessages: Record<VerifyStatus, RequirementConfig> = {
  blocked: {
    canClick: false,
    caption: kycVerifyCaptionMessages.blocked,
    captionColor: "text.fg_warning",
    iconColor: "fg.warning",
    iconName: "Alert",
    name: twoFaFeatureMessages.kycName,
  },
  checking: {
    canClick: false,
    caption: kycVerifyCaptionMessages.checking,
    captionColor: "text.fg_warning",
    iconColor: "fg.warning",
    iconName: "Clock",
    name: twoFaFeatureMessages.kycName,
  },
  completed: {
    canClick: false,
    caption: kycVerifyCaptionMessages.completed,
    captionColor: "text.fg_success",
    iconColor: "fg.success",
    iconName: "Check",
    name: twoFaFeatureMessages.kycName,
  },
  pause: {
    canClick: false,
    caption: kycVerifyCaptionMessages.pause,
    captionColor: "text.fg_warning",
    iconColor: "fg.subtle",
    iconName: "Pause",
    name: twoFaFeatureMessages.kycName,
  },
  rejected: {
    canClick: true,
    caption: kycVerifyCaptionMessages.rejected,
    captionColor: "text.fg_error",
    iconColor: "fg.error",
    iconName: "Alert",
    name: twoFaFeatureMessages.kycName,
  },
  requested: {
    canClick: true,
    caption: kycVerifyCaptionMessages.requested,
    iconColor: "fg.accent",
    iconName: "IdentificationBadge",
    name: twoFaFeatureMessages.kycName,
  },
  revision: {
    canClick: true,
    caption: kycVerifyCaptionMessages.revision,
    captionColor: "text.fg_error",
    iconColor: "fg.error",
    iconName: "Alert",
    name: twoFaFeatureMessages.kycName,
  },
};
const kybVerifyMessages: Record<VerifyStatus, RequirementConfig> = {
  blocked: {
    canClick: false,
    caption: kybVerifyCaptionMessages.blocked,
    captionColor: "text.fg_warning",
    iconColor: "fg.warning",
    iconName: "Alert",
    name: twoFaFeatureMessages.kybName,
  },
  checking: {
    canClick: false,
    caption: kybVerifyCaptionMessages.checking,
    captionColor: "text.fg_warning",
    iconColor: "fg.warning",
    iconName: "Clock",
    name: twoFaFeatureMessages.kybName,
  },
  completed: {
    canClick: false,
    caption: kybVerifyCaptionMessages.completed,
    captionColor: "text.fg_success",
    iconColor: "fg.success",
    iconName: "Check",
    name: twoFaFeatureMessages.kybName,
  },
  pause: {
    canClick: false,
    caption: kybVerifyCaptionMessages.pause,
    captionColor: "text.fg_warning",
    iconColor: "fg.subtle",
    iconName: "Pause",
    name: twoFaFeatureMessages.kybName,
  },
  rejected: {
    canClick: false,
    caption: kybVerifyCaptionMessages.rejected,
    captionColor: "text.fg_error",
    iconColor: "fg.error",
    iconName: "Alert",
    name: twoFaFeatureMessages.kybName,
  },
  requested: {
    canClick: true,
    caption: kybVerifyCaptionMessages.requested,
    iconColor: "fg.accent",
    iconName: "IdentificationBadge",
    name: twoFaFeatureMessages.kybName,
  },
  revision: {
    canClick: false,
    caption: kybVerifyCaptionMessages.revision,
    captionColor: "text.fg_warning",
    iconColor: "fg.warning",
    iconName: "Alert",
    name: twoFaFeatureMessages.kybName,
  },
};

const securityRequirementMap: Record<
  SecurityRequirement,
  SecurityRequirementConfig
> = {
  "kyb-verify": ({ status }) => {
    if (!status) {
      throw new Error("Require status for verification or incorrect status");
    }
    return kybVerifyMessages[status];
  },
  "kyc-verify": ({ status }) => {
    if (!status) {
      throw new Error("Require status for verification or incorrect status");
    }
    return kycVerifyMessages[status];
  },
  offer: ({ offerAccepted, status }) => {
    if (status === "blocked") {
      return blockedConfig(twoFaFeatureMessages.offerName);
    }
    if (offerAccepted) {
      return completedConfig(twoFaFeatureMessages.offerName);
    }
    if (status !== "completed") {
      return {
        canClick: false,
        caption: twoFaFeatureMessages.offerUnavailable,
        iconColor: "fg.subtle",
        iconName: "FileCheck",
        name: twoFaFeatureMessages.offerName,
      };
    }
    return {
      canClick: true,
      caption: twoFaFeatureMessages.offerAccept,
      iconColor: "fg.accent",
      iconName: "FileCheck",
      name: twoFaFeatureMessages.offerName,
    };
  },
  phone: ({ phoneChecked, status }) => {
    if (status === "blocked") {
      return blockedConfig(twoFaFeatureMessages.phoneNumber);
    }
    if (phoneChecked) {
      return {
        canClick: false,
        caption: twoFaFeatureMessages.phoneVerified,
        captionColor: "text.fg_success",
        iconColor: "fg.success",
        iconName: "Check",
        name: twoFaFeatureMessages.phoneNumber,
      };
    }
    return {
      canClick: true,
      caption: twoFaFeatureMessages.phoneUnchecked,
      iconColor: "fg.accent",
      iconName: "Phone",
      name: twoFaFeatureMessages.phoneNumber,
    };
  },
  policies: ({ policiesAccepted, status }) => {
    if (status === "blocked") {
      return blockedConfig(twoFaFeatureMessages.policiesName);
    }
    if (policiesAccepted) {
      return completedConfig(twoFaFeatureMessages.policiesName);
    }
    return {
      canClick: true,
      caption: twoFaFeatureMessages.policiesCaption,
      iconColor: "fg.accent",
      iconName: "FileText",
      name: twoFaFeatureMessages.policiesName,
    };
  },
  questionnaire: ({ questionnairePassed, status }) => {
    if (status === "blocked") {
      return blockedConfig(twoFaFeatureMessages.questionnaireName);
    }
    if (questionnairePassed) {
      return completedConfig(twoFaFeatureMessages.questionnaireName);
    }
    return {
      canClick: true,
      caption: twoFaFeatureMessages.questionnaireCaption,
      iconColor: "fg.accent",
      iconName: "ClipboardText",
      name: twoFaFeatureMessages.questionnaireName,
    };
  },
  "two-fa": ({ authenticatorEnabled, status }) => {
    if (status === "blocked") {
      return blockedConfig(twoFaFeatureMessages.requirementTwoFa);
    }
    if (status === "pause") {
      return {
        canClick: false,
        caption: twoFaFeatureMessages.pauseTwoFa,
        captionColor: "text.fg_warning",
        iconColor: "fg.subtle",
        iconName: "Pause",
        name: twoFaFeatureMessages.requirementTwoFa,
      };
    }
    if (authenticatorEnabled) {
      return {
        canClick: false,
        caption: twoFaFeatureMessages.twoFaConnected,
        captionColor: "text.fg_success",
        iconColor: "fg.success",
        iconName: "Check",
        name: twoFaFeatureMessages.requirementTwoFa,
      };
    }

    return {
      canClick: true,
      caption: twoFaFeatureMessages.googleAuth,
      iconColor: "fg.accent",
      iconName: "Key",
      name: twoFaFeatureMessages.requirementTwoFa,
    };
  },
};

export const SecurityAlert = ({
  completedRequirements,
  description,
  myRefetch,
  requirements,
  security,
  verificationStatus,
  onClick,
}: Props) => {
  const { formatMessage } = useIntl();
  useEffect(() => {
    myRefetch?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const makeHandleClick = (requirement: SecurityRequirement) => () => {
    onClick(requirement);
  };

  const isCompleted = completedRequirements.length === requirements.length;

  const bannerConfigFactory = verificationStatus
    ? bannerConfigMap[verificationStatus]
    : undefined;

  const bannerConfig = bannerConfigFactory
    ? bannerConfigFactory(formatMessage)
    : null;

  return (
    <VStack alignItems="stretch" gap="6" w="full">
      <Flex alignItems="center" justifyContent="center">
        <Flex
          alignItems="center"
          backgroundColor="bg.subtle"
          borderRadius="full"
          h="96px"
          justifyContent="center"
          w="96px"
        >
          <Icon
            color={isCompleted ? "fg.success" : "fg.accent"}
            h="48px"
            name="ShieldDone"
            w="48px"
          />
        </Flex>
      </Flex>
      <VStack gap="1">
        <Typography textStyle="h3">
          <FormattedMessage {...securityAlertMessages.setupTitle} />
        </Typography>
        <Typography textAlign="center" textStyle="body2">
          <FormattedMessage {...description} />
        </Typography>
      </VStack>

      {bannerConfig && <Banner {...bannerConfig} />}

      <CellPanel>
        {requirements.map((requirement) => {
          const configGetter = securityRequirementMap[requirement];
          const config = configGetter({
            ...security,
            status: verificationStatus,
          });
          const statusConfig =
            verificationStatus === "blocked"
              ? blockedConfig(config.name)
              : verificationStatus === "pause"
                ? suspendedConfig(config.name)
                : config;
          return (
            <Cell
              key={requirement}
              caption={
                statusConfig.caption && formatMessage(statusConfig.caption)
              }
              captionColor={statusConfig.captionColor}
              icon={
                <Flex
                  alignItems="center"
                  backgroundColor="bg"
                  borderRadius="full"
                  h="40px"
                  justifyContent="center"
                  w="40px"
                >
                  <Icon
                    color={statusConfig.iconColor}
                    name={statusConfig.iconName}
                    size="lg"
                  />
                </Flex>
              }
              withChevron={statusConfig.canClick}
              onClick={
                statusConfig.canClick ? makeHandleClick(requirement) : undefined
              }
            >
              {formatMessage(statusConfig.name)}
            </Cell>
          );
        })}
      </CellPanel>
    </VStack>
  );
};
